import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, Show } from "solid-js";
import { KeyRound, Loader2, Lock, ShieldCheck, Users } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Skeleton,
  TextField,
} from "@fleet/ui";
import { api, createApiQuery, fetchApi } from "@/lib/api";
import { type components, e2eCrypto } from "@bodega/shared";
import { useI18n } from "@/providers/i18n-provider";
import { QueryErrorState } from "@/components/ui/query-error-state";

type RemoteView = components["schemas"]["RemoteView"];
type ErrorModel = components["schemas"]["ErrorModel"];
type IdentityEnvelope = components["schemas"]["E2eIdentityEnvelope"];
type ShareEnvelope = components["schemas"]["TeamKeyShareEnvelope"];

/**
 * Distribution of the E2E team key for an organization BYOS remote (kit
 * `e2e-crypto`, Boveda canon). ALL cryptography runs in the browser:
 * the ECDH pair is generated here, the private one travels wrapped by the passphrase
 * (Argon2id → AES-GCM) and the team key is uploaded sealed to each member's public key
 * (ECIES). The server only guards opaque envelopes.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export function TeamKeySection(props: { remote: RemoteView }) {
  const { t } = useI18n();

  const [passphrase, setPassphrase] = createSignal("");
  const [busy, setBusy] = createSignal<"identity" | "distribute" | "unlock" | null>(null);
  const [error, setError] = createSignal<string | null>(null);
  const [fingerprint, setFingerprint] = createSignal<string | null>(null);
  const [distributed, setDistributed] = createSignal<{ sealed: number; pending: number } | null>(
    null,
  );

  const identity = createApiQuery<IdentityEnvelope | null, ErrorModel>(
    () => ["byos", "e2e-identity"],
    async () => api.GET("/api/byos/e2e/identity", {}),
  );

  const myShare = createApiQuery<ShareEnvelope | null, ErrorModel>(
    () => ["byos", "e2e-share", props.remote.id],
    async () =>
      api.GET("/api/byos/remotes/{id}/e2e/shares/me", {
        params: { path: { id: props.remote.id } },
      }),
  );

  const hasIdentity = createMemo(() => Boolean(identity.isSuccess && identity.data?.identity));
  const shareEpoch = createMemo(() => myShare.data?.share?.key_epoch ?? null);

  const fail = (e: unknown) => {
    // WebCrypto rejects with a DOMException with an EMPTY message (e.g. OperationError
    // when opening with an incorrect passphrase): without fallback the alert would not appear.
    const text = (e instanceof Error ? e.message : String(e)).trim();
    setError(text || t(msg`Could not unlock — check your passphrase.`));
  };

  /** Creates the user's own E2E identity and uploads it (opaque blobs). */
  const createIdentity = async () => {
    setError(null);
    setBusy("identity");
    try {
      const blobs = await e2eCrypto.createE2eIdentity(passphrase());
      await fetchApi(
        api.PUT("/api/byos/e2e/identity", {
          body: {
            public_key: blobs.publicKey,
            protected_private_key: blobs.protectedPrivateKey,
            kdf_salt: blobs.kdfSalt,
            kdf_params: blobs.kdfParams,
          },
        }),
      );
      await identity.refetch();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  /**
   * Generates a NEW team key (current epoch + 1 = rotation) and seals it
   * to each organization member who already has an E2E identity.
   /** Waits for the app to finish booting (skeleton overlay disappears). */
  const distribute = async () => {
    setError(null);
    setBusy("distribute");
    setDistributed(null);
    try {
      const members = await fetchApi(
        api.GET("/api/byos/remotes/{id}/e2e/members", {
          params: { path: { id: props.remote.id } },
        }),
      );
      const ready = (members ?? []).filter(
        (m): m is typeof m & { public_key: string } => typeof m.public_key === "string",
      );
      const pending = (members ?? []).length - ready.length;
      if (ready.length === 0) {
        setError(t(msg`No teammate has an E2E identity yet.`));
        return;
      }
      const epoch = (shareEpoch() ?? 0) + 1;
      const teamKey = e2eCrypto.createTeamKey();
      const shares = await e2eCrypto.distributeTeamKey(
        props.remote.id,
        epoch,
        teamKey,
        ready.map((m) => ({ userId: m.user_id, publicKey: m.public_key })),
      );
      await fetchApi(
        api.PUT("/api/byos/remotes/{id}/e2e/shares", {
          params: { path: { id: props.remote.id } },
          body: {
            key_epoch: epoch,
            shares: shares.map((s) => ({ user_id: s.userId, sealed_key: s.sealedKey })),
          },
        }),
      );
      setDistributed({ sealed: ready.length, pending });
      setFingerprint(await e2eCrypto.teamKeyFingerprint(teamKey));
      await myShare.refetch();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  /** Unlocks the identity with the passphrase and opens the sealed portion owned by the user. */
  const unlock = async () => {
    setError(null);
    setBusy("unlock");
    setFingerprint(null);
    try {
      const own = identity.isSuccess ? identity.data?.identity : undefined;
      if (!own) {
        setError(t(msg`Create your E2E identity first.`));
        return;
      }
      const envelope = await fetchApi(
        api.GET("/api/byos/remotes/{id}/e2e/shares/me", {
          params: { path: { id: props.remote.id } },
        }),
      );
      const share = envelope?.share;
      if (!share) {
        setError(t(msg`No team key has been distributed to you yet.`));
        return;
      }
      const priv = await e2eCrypto.unlockE2eIdentity(passphrase(), {
        publicKey: own.public_key,
        protectedPrivateKey: own.protected_private_key,
        kdfSalt: own.kdf_salt,
        // Opaque custody: params come back exactly as uploaded.
        // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
        kdfParams: own.kdf_params as e2eCrypto.Argon2Params,
      });
      const teamKey = await e2eCrypto.openTeamKeyShare(priv, {
        remoteId: share.remote_id,
        keyEpoch: share.key_epoch,
        sealedKey: share.sealed_key,
      });
      setFingerprint(await e2eCrypto.teamKeyFingerprint(teamKey));
      await myShare.refetch();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card data-testid="e2e-team-key">
      <CardHeader>
        <CardTitle class="flex items-center gap-2 text-base">
          <KeyRound class="h-4 w-4 text-primary" />
          {t(msg`Team E2E key`)}
          <Show when={shareEpoch() !== null}>
            <Badge variant="secondary" data-testid="e2e-share-status">
              {t(msg`Key epoch`)} {shareEpoch()}
            </Badge>
          </Show>
        </CardTitle>
      </CardHeader>
      <CardContent class="space-y-3">
        <p class="text-sm text-muted-foreground">
          {t(
            msg`End-to-end key for this team remote: it is generated in your browser, sealed to each teammate's public key, and the server only stores opaque envelopes.`,
          )}
        </p>
        <Show when={identity.isError}>
          <QueryErrorState
            testId="byos-team-key-error"
            compact
            onRetry={() => void identity.refetch()}
          />
        </Show>
        <Show when={!identity.isError}>
          <Show when={!identity.isLoading} fallback={<Skeleton class="h-16 w-full rounded-xl" />}>
            <div class="flex flex-wrap items-end gap-2">
              <div class="min-w-[12rem] flex-1 space-y-2">
                <Label for="e2e-passphrase" class="text-xs font-semibold">
                  {t(msg`E2E passphrase`)}
                </Label>
                <TextField
                  id="e2e-passphrase"
                  type="password"
                  value={passphrase()}
                  onInput={(e) => setPassphrase(e.currentTarget.value)}
                  placeholder={t(msg`Never sent to the server`)}
                  data-testid="e2e-passphrase"
                />
              </div>
              <Show
                when={hasIdentity()}
                fallback={
                  <Button
                    onClick={() => void createIdentity()}
                    disabled={passphrase().length < 8 || busy() !== null}
                    data-testid="e2e-identity-create"
                    class="gap-2"
                  >
                    <Show when={busy() === "identity"} fallback={<ShieldCheck class="h-4 w-4" />}>
                      <Loader2 class="h-4 w-4 animate-spin" />
                    </Show>
                    {t(msg`Create E2E identity`)}
                  </Button>
                }
              >
                <Button
                  variant="outline"
                  onClick={() => void distribute()}
                  disabled={busy() !== null}
                  data-testid="e2e-distribute"
                  class="gap-2"
                >
                  <Show when={busy() === "distribute"} fallback={<Users class="h-4 w-4" />}>
                    <Loader2 class="h-4 w-4 animate-spin" />
                  </Show>
                  {t(msg`Distribute team key`)}
                </Button>
                <Button
                  onClick={() => void unlock()}
                  disabled={busy() !== null}
                  data-testid="e2e-unlock"
                  class="gap-2"
                >
                  <Show when={busy() === "unlock"} fallback={<Lock class="h-4 w-4" />}>
                    <Loader2 class="h-4 w-4 animate-spin" />
                  </Show>
                  {t(msg`Unlock team key`)}
                </Button>
              </Show>
            </div>
            <Show when={hasIdentity() && passphrase().length > 0 && passphrase().length < 8}>
              <p class="text-xs text-muted-foreground">
                {t(msg`Passphrases have at least 8 characters.`)}
              </p>
            </Show>
          </Show>
        </Show>
        <Show when={distributed()}>
          {(d) => (
            <p class="text-sm" data-testid="e2e-distributed">
              {t(msg`Key sealed to`)} {d().sealed} {t(msg`member(s)`)}
              <Show when={d().pending > 0}>
                <span class="text-muted-foreground">
                  {" · "}
                  {d().pending} {t(msg`without E2E identity yet`)}
                </span>
              </Show>
            </p>
          )}
        </Show>
        <Show when={fingerprint()}>
          <p class="flex items-center gap-2 text-sm" data-testid="e2e-key-fingerprint">
            <ShieldCheck class="h-4 w-4 text-primary" />
            {t(msg`Team key unlocked — fingerprint`)}{" "}
            <code class="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{fingerprint()}</code>
          </p>
        </Show>
        <Show when={error()}>
          <p class="text-sm text-destructive" role="alert" data-testid="e2e-error">
            {error()}
          </p>
        </Show>
      </CardContent>
    </Card>
  );
}
